/* eslint-disable no-console */
import { ParsedUrlQuery } from 'querystring';
import React, { useEffect } from 'react';
import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import { Layout } from '$layouts/layout';
import type { ICMSPage } from '$models';
import { PageTypes } from '$constants/pages';
import { ServerSidePropsWithFrameFunc, SSPResult } from '$lib/serversideprops-with-frame';
import { ProductPage } from '$templates/product-page/product-page';
import { getProductPageSSRProps } from '$templates/product-page/products-page-ssr.helper';
import { useAuthentication, useCMSRoute } from '~/store';
import { StructuredData } from '$components/elements/structured-data';
import { convertStructuredProduct } from '$lib/helpers/structured-data.helper';
import { StructuredDataKey } from '$constants/structured-data';
import { ImageRichTextpage } from '$templates';
import { getPageTypeByData } from '$lib/helpers';

type Props = {
    page: ICMSPage;
    isCrawler: boolean;
    pageSpecificProps: ReturnType<typeof getProductPageSSRProps>;
    query: ParsedUrlQuery;
    productStructuredData?: Record<string, unknown> | null;
};

const PAGES: { [key: string]: React.ComponentType<any> } = {
    [PageTypes.ProductPage]: ProductPage,
    [PageTypes.Error]: ImageRichTextpage,
};

const DynamicPage = (props: SSPResult<Props>): JSX.Element => {
    const { navigation, page, pageSpecificProps, productStructuredData, resolvedUrl, routes, query, ...rest } = props;
    const { setRoutes } = useCMSRoute();
    const { setShowMemberAccessPanel } = useAuthentication();
    const pageType = getPageTypeByData(page);

    useEffect(() => {
        if (query.authentication === 'failed') {
            setShowMemberAccessPanel('login', query.returnUrl.toString());
        }
    }, [query]);

    useEffect(() => {
        setRoutes(routes);
    }, [routes, setRoutes]);

    const PAGE = pageType ? PAGES[pageType] : undefined;

    return (
        <>
            {productStructuredData && (
                <StructuredData structuredDataKey={StructuredDataKey.Product} jsonData={productStructuredData} />
            )}
            <Layout
                title={page.title ?? ''}
                navigation={navigation}
                metaData={page.metaHtmlHeadModel}
                breadcrumbs={page.breadCrumb}
            >
                {!!PAGE && <PAGE navigation={navigation} {...page} {...pageSpecificProps} {...rest} />}
            </Layout>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = ServerSidePropsWithFrameFunc<Partial<Props>>(
    async (context, page, _, isCrawler): Promise<GetServerSidePropsResult<Partial<Props>>> => {
        const { res, resolvedUrl, query } = context;

        const mapPageHeaders: string[] = [];

        if (isCrawler) {
            res.setHeader('Cache-Control', `private, max-age=600, must-revalidate`);
        }
        try {
            const { data: pageData, status, headers } = page;

            if (!pageData && status === 500) {
                throw new Error('Internal Server Error');
            }

            if (!pageData) {
                return {
                    notFound: true,
                };
            }

            calculateRoughlyInViewOnClient(pageData);

            // Map headers from page response.
            // Allows the CMS to handle how pages are cached etc.
            mapPageHeaders.forEach((headerKey) => {
                const header = headers.get(headerKey);
                header && res.setHeader(headerKey, header);
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let pageSpecificProps: any = {};

            if (status < 300) {
                overridePageData(pageData, 'deliveryInformation', PageTypes.ProductPage);
                pageSpecificProps = await getProductPageSSRProps(pageData, query);

                if (
                    pageSpecificProps &&
                    // eslint-disable-next-line no-prototype-builtins
                    (pageSpecificProps.hasOwnProperty('redirect') || pageSpecificProps.hasOwnProperty('notFound'))
                ) {
                    return pageSpecificProps;
                }
            }

            return {
                props: {
                    page: pageData,
                    pageSpecificProps,
                    productStructuredData: convertStructuredProduct(pageSpecificProps?.selectedProduct),
                    isCrawler,
                    query,
                },
            };
        } catch (error) {
            console.error(`Failed loading: ${resolvedUrl}`);
            console.error(error);
            res.statusCode = 500;
            throw error;
        }
    }
);

function overridePageData(page: ICMSPage, dataName: string, pageType: PageTypes) {
    const pages = page.content.filter((x) => x[dataName]);
    if (!pages?.length) {
        console.error(`Missing page data for: ${pageType}`);
    } else {
        pages.forEach((x) => (x.spotName = pageType));
    }
}

function calculateRoughlyInViewOnClient(pageData: ICMSPage) {
    const inView = 4;
    let currentCount = 0;
    [...pageData.content, ...pageData.bottomContent].forEach((x) => {
        // if page take it as a full page view.
        if (Object.keys(PageTypes).some((t) => t === x.spotName)) {
            currentCount += inView;
        }
        // else approximate size from width in the data.
        switch (x.width) {
            case 'small':
                currentCount += 0.25;
                break;
            case 'medium':
                currentCount += 0.33;
                break;
            case 'large':
                currentCount += 0.5;
                break;
            case 'full':
                currentCount += 1;
                break;
            default:
                currentCount += 1;
        }

        x.eagerLoad = currentCount <= inView;
    });
}

export default DynamicPage;
